import { useState } from 'react';
import Autocomplete from './Autocomplete';
import logger from '../../utils/logger';
import { BoxProps } from '@mui/material';
import { CategorySuggestion, EquipmentClassSuggestion, SearchSuggetion } from '../../types/search';
import { getSearchAutoSuggestOptions } from '../../apis/search';
import { Subject } from 'rxjs';
import { v4 as uuid } from 'uuid';
import {
  AutoSuggestSearchOptionType,
  AutocompleteOption,
  AutocompleteOptionGroup,
  AutocompleteOptionType,
  AutocompletePopupPosition,
  SearchAutoSuggestOption,
  AutocompleteSelectableOption,
} from '../../types/autocomplete';
import {
  DEFAULT_AUTOSUGGEST_RESULT_SIZE,
  SEARCH_DEFAULT_DEBOUNCE_TIME,
} from '../../constants/search';
import {
  redirectToCategoryPage,
  redirectToEquipmentClass,
  redirectToTextSearchResultsPage,
} from '../../utils/router';

interface SearchAutoSuggestProps extends BoxProps {
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  helperText?: JSX.Element;
  popupPosition?: AutocompletePopupPosition;
  placeholder?: string;
  inputFocusSubject?: Subject<boolean>;
  closeAutocompleteSubject?: Subject<boolean>;
  onDropdownToggleFn?: (popupState: boolean) => void;
  onInputChangeCallBack?: (value: string) => void;
}

function SearchAutoSuggest({
  startIcon,
  endIcon,
  popupPosition,
  placeholder,
  helperText,
  inputFocusSubject,
  closeAutocompleteSubject,
  onDropdownToggleFn,
  onInputChangeCallBack,
  ...rest
}: SearchAutoSuggestProps) {
  const onEnterInInputField = (value: string) => {
    value = value.trim();
    if (value) {
      redirectToTextSearchResultsPage(value);
    }
  };

  const selectHandler = async (value: SearchAutoSuggestOption) => {
    if (value.optionType === AutoSuggestSearchOptionType.EQUIPMENTCLASS) {
      redirectToEquipmentClass(value.slug);
    }
    if (value.optionType === AutoSuggestSearchOptionType.CATEGORIES) {
      redirectToCategoryPage(value.slug);
    }
  };

  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const getOptions = async (value: string): Promise<AutocompleteOptionGroup[]> => {
    if (!value) {
      return [];
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
      setDebounceTimeout(null);
    }

    return new Promise<AutocompleteOptionGroup[]>((resolve) => {
      const newTimeout = setTimeout(async () => {
        let fetchedOptions: AutocompleteOptionGroup[] = [];

        try {
          const fetchedApiData: SearchSuggetion = await getSearchAutoSuggestOptions(
            value,
            DEFAULT_AUTOSUGGEST_RESULT_SIZE,
          );

          if (
            fetchedApiData.equipment_suggestions.length === 0 &&
            fetchedApiData.category_suggestions.length === 0
          ) {
            // Return empty options if no results
            fetchedOptions = [];
          } else {
            const equipmentClassGroupItems: AutocompleteOption[] =
              fetchedApiData.equipment_suggestions.map((e: EquipmentClassSuggestion) => ({
                id: uuid(),
                label: e.text,
                type: AutocompleteOptionType.STANDARD,
                slug: e.equipment_class_slug,
                optionType: AutoSuggestSearchOptionType.EQUIPMENTCLASS,
              }));

            const equipmentClassOptionsGroup: AutocompleteOptionGroup = {
              id: uuid(),
              groupLabel: 'equipment',
              groupItems: equipmentClassGroupItems,
            };

            const categoryGroupItems: AutocompleteOption[] =
              fetchedApiData.category_suggestions.map((e: CategorySuggestion) => ({
                id: uuid(),
                label: e.text,
                type: AutocompleteOptionType.STANDARD,
                slug: e.category_slug,
                optionType: AutoSuggestSearchOptionType.CATEGORIES,
              }));

            const categoryOptionsGroup: AutocompleteOptionGroup = {
              id: uuid(),
              groupLabel: 'categories',
              groupItems: categoryGroupItems,
            };

            fetchedOptions = [equipmentClassOptionsGroup, categoryOptionsGroup];
          }
        } catch (error) {
          logger('error', 'Error fetching locations', error);
        }

        resolve(fetchedOptions);

        // Set debounceTimeout back to null only after resolving the promise
        setDebounceTimeout(null);
      }, SEARCH_DEFAULT_DEBOUNCE_TIME);

      setDebounceTimeout(newTimeout);
    });
  };

  return (
    <Autocomplete
      value={null}
      startIcon={startIcon}
      endIcon={endIcon}
      selectHandler={async (selectedOption: AutocompleteSelectableOption) => {
        if (selectedOption !== null) {
          await selectHandler(selectedOption as SearchAutoSuggestOption);
        }
      }}
      getOptionsHandler={getOptions}
      isLoading={false}
      popupPosition={popupPosition ?? 'absolute'}
      placeholder={placeholder ?? ''}
      helperText={helperText}
      onDropdownToggleFn={onDropdownToggleFn}
      overRideOnEnterInInputField={onEnterInInputField}
      onClick={() => inputFocusSubject?.next(true)}
      inputFocusSubject={inputFocusSubject}
      closeAutocompleteSubject={closeAutocompleteSubject}
      onInputChangeCallBack={onInputChangeCallBack}
      {...rest}
    />
  );
}

export default SearchAutoSuggest;
